import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { captureOwnerInboxEntry } from "../../../../services/ownerInbox.service";
import { OwnerVoiceFormControls, useOwnerVoiceForm, voiceFieldProps } from "../voice/OwnerVoiceOperatorContext";

function normalizeLocalDateTime(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function OwnerDayNoteDialog({ open, occurredAt = "", onClose, onSaved }) {
  const [form, setForm] = React.useState({ text: "", occurredAt: "" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setForm({ text: "", occurredAt: normalizeLocalDateTime(occurredAt || new Date()) });
    setError("");
  }, [open, occurredAt]);

  const voice = useOwnerVoiceForm({
    id: "day-note",
    open: Boolean(open),
    config: {
      title: "nota del día",
      values: form,
      setValue: (name, value) => setForm((current) => ({ ...current, [name]: value })),
      fields: [
        { name: "text", label: "qué pasó", prompt: "Contame qué pasó o qué querés recordar de este momento.", required: true, aliases: ["nota", "que paso", "qué pasó", "anotacion", "anotación"] },
        { name: "occurredAt", label: "cuándo", prompt: "¿A qué hora pasó? Podés decir ahora o una hora concreta.", kind: "datetime", required: true, aliases: ["cuando", "cuándo", "hora"] },
      ],
      onSubmit: () => submit(),
      onCancel: onClose,
      successMessage: "Nota guardada en tu día.",
    },
  });

  async function submit() {
    const text = form.text.trim();
    if (!text) { setError("Contame qué querés dejar anotado."); return false; }
    const when = new Date(form.occurredAt);
    if (Number.isNaN(when.getTime())) { setError("La hora de la nota no es válida."); return false; }

    setSaving(true);
    setError("");
    try {
      const response = await captureOwnerInboxEntry(ownerApi, {
        text,
        kind: "note",
        occurredAt: when.toISOString(),
        inputSource: voice?.handsFree ? "voice" : "text",
      });
      await onSaved?.(response?.entry);
      onClose?.();
      return true;
    } catch (err) {
      setError(formatApiError(err, "No pude guardar esta nota del día."));
      return false;
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000 }}>Anotar en el día</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "text.secondary", fontWeight: 720, fontSize: 12.5, mb: 1.2 }}>
          Esto queda en la línea de tiempo. Si después resulta ser una tarea, seguimiento o proyecto, podés estructurarlo sin perder la nota original.
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        <Stack spacing={1.15}>
          <TextField
            inputProps={voiceFieldProps("text")}
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Qué pasó / qué querés recordar"
            placeholder="Ej. Le respondí a Agustín. Me pidió las credenciales y quiero decidir después si esto se transforma en una tarea."
            value={form.text}
            onChange={(event) => setForm((current) => ({ ...current, text: event.target.value }))}
          />
          <TextField
            inputProps={voiceFieldProps("occurredAt")}
            fullWidth
            type="datetime-local"
            label="Cuándo pasó"
            InputLabelProps={{ shrink: true }}
            value={form.occurredAt}
            onChange={(event) => setForm((current) => ({ ...current, occurredAt: event.target.value }))}
          />
          <OwnerVoiceFormControls label="Completar nota hablando" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Guardando…" : "Guardar en mi día"}</Button>
      </DialogActions>
    </Dialog>
  );
}
